"use client";

import Image from "next/image";
import Link from "next/link";
import type { Car } from "@/types/Car";

interface CarCardProps {
  car: Car;
}

export default function CarCard({ car }: CarCardProps) {
  // const isUnavailable =
  //   (car.status || "").toLowerCase() === "booked" ||
  //   (car.status || "").toLowerCase() === "unavailable";
  const isUnavailable = (car.status || "").toLowerCase() !== "available";

  return (
    <div className="group relative rounded-2xl bg-white border border-gray-100 shadow-sm overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
      {/* Image */}
      <div className="relative overflow-hidden">
        {/* Status badge */}
        <div className="absolute left-3 top-3 z-10">
          <span
            className={`inline-flex items-center gap-1 px-2.5 py-0.5 text-[10px] font-bold tracking-wide uppercase rounded-full shadow-sm backdrop-blur-sm ${
              isUnavailable
                ? "bg-red-500/90 text-white"
                : "bg-emerald-500/90 text-white"
            }`}
          >
            <span
              className={`h-1.5 w-1.5 rounded-full ${
                isUnavailable ? "bg-red-200" : "bg-emerald-200"
              }`}
            />
            {isUnavailable ? "Unavailable" : "Available"}
          </span>
        </div>

        {car.image ? (
          <div className="relative aspect-[16/10] w-full bg-gray-100">
            <Image
              src={car.image}
              alt={car.name}
              fill
              sizes="(max-width: 640px) 100vw, 33vw"
              className="object-cover transition-transform duration-500 group-hover:scale-105"
            />
            {/* Subtle gradient overlay at bottom of image */}
            <div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-black/20 to-transparent" />
          </div>
        ) : (
          <div className="aspect-[16/10] flex flex-col items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 gap-2">
            <svg
              className="w-8 h-8 text-gray-300"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12"
              />
            </svg>
            <span className="text-xs text-gray-400 font-medium">No Image</span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Name */}
        <h3 className="text-sm font-bold text-gray-900 line-clamp-1 leading-snug">
          {car.name}
        </h3>

        {/* Specs row */}
        <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
          {car.fuelType && (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-500 bg-slate-50 border border-slate-100 px-2 py-0.5 rounded-full">
              <svg
                className="w-2.5 h-2.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
              {car.fuelType.trim()}
            </span>
          )}
          {car.transmission && (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-500 bg-slate-50 border border-slate-100 px-2 py-0.5 rounded-full">
              <svg
                className="w-2.5 h-2.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5"
                />
              </svg>
              {car.transmission.trim()}
            </span>
          )}
        </div>

        {/* Divider */}
        <div className="my-3 border-t border-gray-100" />

        {/* Action buttons */}
        <div className="flex items-center gap-2">
          <Link href={`/vehicles/${car._id}`} className="flex-1">
            <button className="w-full rounded-xl border border-blue-900/20 bg-white px-3 py-2 text-[11px] font-bold text-blue-900 hover:bg-blue-50 hover:border-blue-900/40 transition-all duration-200">
              Details
            </button>
          </Link>

          {isUnavailable ? (
            <button
              disabled
              className="flex-1 cursor-not-allowed rounded-xl bg-gray-100 px-3 py-2 text-[11px] font-bold text-gray-400"
            >
              Unavailable
            </button>
          ) : (
            <Link href={`/vehicles/${car._id}/book`} className="flex-1">
              <button className="w-full rounded-xl bg-blue-800 px-3 py-2 text-[11px] font-bold text-white hover:bg-blue-700 active:scale-95 transition-all duration-200 shadow-sm shadow-blue-900/20">
                Book Now
              </button>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
